import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import {
  Message, Table, Button, Icon,
} from 'semantic-ui-react';
import { Field, formValueSelector, change } from 'redux-form';
import {
  get,
  range,
  map,
  toString,
  trim,
  toNumber,
  isNaN,
  isNumber,
  toSafeInteger,
  isEmpty,
} from 'lodash';
import Clipboard from 'react-clipboard.js';

import client from '../../apollo';
import TextInput from './TextInput';
import DateInput from './DateInput';
import SelectDropdown from './SelectDropdown';
import LoadingDataEnhancer from '../../containers/LoadingData';
import ftbLocationsQuery from '../../graphql/ftbLocationsQuery';
import decodeVinQuery from '../../graphql/decodeVinQuery';
import dupeVehicleQuery from '../../graphql/dupeVehicleQuery';
import dupeExternalVehicleQuery from '../../graphql/dupeExternalVehicleQuery';
import dealerInformationQuery from '../../graphql/dealerInformationQuery';
import {
  addDupeVehicle,
  removeDupeVehicle,
} from '../../data/duplicateVehicles/actions';
import {
  addDupeExternalVehicle,
  removeDupeExternalVehicle,
} from '../../data/duplicateExternalVehicles/actions';
import {
  addDupeEntryVehicle,
  removeDupeEntryVehicle,
  clearDupeEntryVehicle,
} from '../../data/duplicateEntryVehicles/actions';
import { removeDupeVehicleIndex } from '../../data/duplicateVehicleIndex/actions';
import { removeDupeExternalVehicleIndex } from '../../data/duplicateExternalVehicleIndex/actions';
import { removeDupeEntryVehicleIndex } from '../../data/duplicateEntryVehicleIndex/actions';

const yearOptions = () => {
  const yearOptionGen = year => {
    return { key: year, value: year, text: toString(year) };
  };
  const years = range(moment().year() + 1, 1996);
  return map(years, yearOptionGen);
};

const locationOptionGen = ftbLocation => {
  const { id, name } = ftbLocation;
  return { key: id, value: name, text: toString(name) };
};

const fieldRequired = value => (value ? undefined : '* Required');
const validDealer = value => (value ? undefined : 'Invalid Dealer');
const alphaNumeric = value => (value && /[^a-zA-Z0-9 ]/i.test(value)
  ? 'Only alphanumeric characters'
  : undefined);

const hasLength = len => value => (value && value.length !== len ? `Must be ${len} characters` : undefined);
const hasLength17 = hasLength(17);

const maxLength = max => value => (value && value.length > max ? `Must be ${max} characters or less` : undefined);
const maxLength4 = maxLength(4);
const maxLength5 = maxLength(5);
const maxLength6 = maxLength(6);

const shouldBeNumber = value => (value && isNaN(toNumber(value)) ? 'Must be a number' : undefined);
const shouldBe5mil = value => (value && !/^5[0-9]{6,6}$/i.test(value)
  ? 'Must be a Dealer Number'
  : undefined);
const shouldBeEmail = value => (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
  ? 'Invalid email address'
  : undefined);

const purchaseDateValidation = () => value => {
  if (value) {
    if (moment(value).isBefore(moment().subtract(19, 'days'))) {
      return 'Date older than 19 days';
    }

    if (moment(value).isAfter(moment())) {
      return 'Future date entered';
    }
  }
  return undefined;
};

const NplusFieldRow = props => {
  const {
    fields,
    vehicles,
    dispatch,
    duplicateVehicleIndex,
    duplicateExternalVehicleIndex,
    duplicateEntryVehicleIndex,
    data: { ftbLocations },
    isVehiclePurchasePriceOverMmr,
  } = props;

  const [vinError, setVinError] = useState('');
  const [dealerErr, setDealerErr] = useState(undefined);
  const [gettingInfo, setGettingInfo] = useState('');

  const onSuccess = useCallback(() => {
    setGettingInfo('VIN copied');
  }, []);

  const getText = useCallback(() => {
    const vinsArray = [];
    if (vehicles) {
      vehicles.forEach(vehicle => {
        vinsArray.push(vehicle.vin);
      });
    }
    return vinsArray.join('\n');
  }, [vehicles]);

  const locationOptions = map(ftbLocations, locationOptionGen);

  const TableHeader = (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>
          VIN
          <Clipboard option-text={getText} onSuccess={onSuccess}>
            <Icon name='clipboard' style={{ cursor: 'pointer' }} />
          </Clipboard>
        </Table.HeaderCell>
        <Table.HeaderCell style={{ width: '10px' }}>Year</Table.HeaderCell>
        <Table.HeaderCell>Make</Table.HeaderCell>
        <Table.HeaderCell>Model</Table.HeaderCell>
        <Table.HeaderCell>Odometer</Table.HeaderCell>
        <Table.HeaderCell>Dealer#</Table.HeaderCell>
        <Table.HeaderCell>Dealer Name</Table.HeaderCell>
        <Table.HeaderCell>Seller Name</Table.HeaderCell>
        <Table.HeaderCell>Purchase Price</Table.HeaderCell>
        <Table.HeaderCell>Buy Fee</Table.HeaderCell>
        <Table.HeaderCell>Vehicle Total</Table.HeaderCell>
        <Table.HeaderCell>Location</Table.HeaderCell>
        <Table.HeaderCell>Email Address</Table.HeaderCell>
        <Table.HeaderCell>Guarantee Purchase Date</Table.HeaderCell>
        <Table.HeaderCell />
      </Table.Row>
    </Table.Header>
  );

  if (!isEmpty(duplicateVehicleIndex)) {
    duplicateVehicleIndex.map(item => {
      const existingVehicle = vehicles.filter(
        vehicleItem => vehicleItem.vin === item.vin
          && vehicleItem.dealerNo === item.dealerNumber
      );
      if (!isEmpty(existingVehicle)) {
        fields.remove(item.index);
        dispatch(
          removeDupeVehicleIndex({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
        dispatch(
          removeDupeVehicle({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
      }
      return {};
    });
  }

  if (!isEmpty(duplicateExternalVehicleIndex)) {
    duplicateExternalVehicleIndex.map(item => {
      const existingVehicle = vehicles.filter(
        vehicleItem => vehicleItem.vin === item.vin
          && vehicleItem.dealerNo === item.dealerNumber
      );
      if (!isEmpty(existingVehicle)) {
        fields.remove(item.index);
        dispatch(
          removeDupeExternalVehicleIndex({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
        dispatch(
          removeDupeExternalVehicle({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
      }
      return {};
    });
  }

  if (!isEmpty(duplicateEntryVehicleIndex)) {
    duplicateEntryVehicleIndex.map(item => {
      const existingVehicle = vehicles.filter(
        vehicleItem => vehicleItem.vin === item.vin
          && vehicleItem.dealerNo === item.dealerNumber
      );
      if (!isEmpty(existingVehicle)) {
        fields.remove(item.index);
        dispatch(
          removeDupeEntryVehicleIndex({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
        dispatch(
          removeDupeEntryVehicle({
            vin: item.vin,
            dealerNumber: item.dealerNumber,
          })
        );
      }
      return {};
    });
  }

  const calculateSum = (currentVehicles, index, currentDispatch) => () => {
    const vehicle = currentVehicles[index];
    const { vehiclePurchasePrice } = vehicle;
    const { buyFee } = vehicle;
    const totalVal = toSafeInteger(buyFee) + toSafeInteger(vehiclePurchasePrice);

    if (buyFee || vehiclePurchasePrice) {
      currentDispatch(
        change(
          'npForm',
          `vehicles[${index}].vehicleTotal`,
          toString(totalVal)
        )
      );
    }
  };

  const getDealerName = (currentVehicles, index, currentDispatch) => {
    const vehicle = currentVehicles[index];
    const dealerNumber = vehicle.dealerNo;
    if (dealerNumber) {
      currentDispatch(change('npForm', `vehicles[${index}].dealerName`, ''));
      setVinError('');
      setGettingInfo('Fetching Dealer Name ...');
      setDealerErr('');

      client
        .query({
          query: dealerInformationQuery,
          variables: { dealerNumber },
        })
        .then(({ data }) => {
          setVinError('');
          setGettingInfo('');
          setDealerErr('');
          const dealerName = get(data, 'dealerInformation.dealerName');
          if (dealerName) {
            currentDispatch(
              change('npForm', `vehicles[${index}].dealerName`, dealerName)
            );
          } else {
            setDealerErr('Invalid Dealer');
            currentDispatch(change('npForm', `vehicles[${index}].dealerName`, ''));
          }
        })
        .catch(() => {
          setDealerErr('Invalid Dealer');
        });
    }
  };
  const parseValueToInt = value => {
    if (value && isNumber(value)) {
      return parseInt(value, 10);
    }
    return value;
  };
  const trimValue = value => {
    if (value) {
      return trim(value.toUpperCase());
    }
    return value.toUpperCase();
  };
  const handleDismiss = () => {
    setVinError('');
    setGettingInfo('');
    setDealerErr(undefined);
  };
  const decodeVin = (currentVehicles, index, currentDispatch) => {
    const vehicle = currentVehicles[index];
    const { vin } = vehicle;
    if (vin && vin.length > 0) {
      setVinError('');
      setGettingInfo('Fetching VIN info ...');
      client
        .query({
          query: decodeVinQuery,
          variables: { vin },
        })
        .then(({ data }) => {
          setGettingInfo('');
          const {
            decodeVin: {
              year, make, model, errors,
            },
          } = data;
          const currentYearValue = vehicle.year;
          if (!(currentYearValue && currentYearValue > 0)) {
            currentDispatch(change('npForm', `vehicles[${index}].year`, year || ''));
          }
          const currentMakeValue = vehicle.make;
          if (!(currentMakeValue && currentMakeValue.length > 0)) {
            currentDispatch(change('npForm', `vehicles[${index}].make`, make || ''));
          }
          const currentModelValue = vehicle.model;
          if (!(currentModelValue && currentModelValue.length > 0)) {
            currentDispatch(
              change('npForm', `vehicles[${index}].model`, model || '')
            );
          }

          if (errors) {
            setVinError(errors);
          }
        })
        .catch(error => {
          setVinError(`Error fetching info for ${vin} - ${error}`);
        });
    }
  };

  const findDupeVehicle = (currentVehicles, index, currentDispatch) => {
    const vehicle = currentVehicles[index];
    const { vin } = vehicle;
    const dealerNumber = vehicle.dealerNo;
    const isVinPresent = vin && vin.length > 0;
    const isDealerPresent = dealerNumber && dealerNumber.length > 0;

    if (isVinPresent && isDealerPresent) {
      client
        .query({
          query: dupeVehicleQuery,
          variables: { vin, dealerNumber },
        })
        .then(({ data }) => {
          const {
            errors,
            dupeVehicle: {
              guaranteeOffer: {
                dealerName,
                guaranteedAt,
                auctionInformation: {
                  dealerNumber: auctionDealerNumber,
                  auctionLocation: { name },
                },
              },
            },
          } = data;
          const dupeVehicleObject = {
            vin,
            dealerName,
            guaranteedAt,
            dealerNumber: toString(auctionDealerNumber),
            auctionName: name,
          };
          currentDispatch(addDupeVehicle(dupeVehicleObject));
          if (errors) {
            setVinError(errors);
          }
        })
        .catch(error => {
          setVinError(`Error fetching info for ${vin} - ${error}`);
        });
    }
  };

  const findDupeExternalVehicle = (currentVehicles, index, currentDispatch) => {
    const vehicle = currentVehicles[index];
    const { vin } = vehicle;
    const dealerNumber = vehicle.dealerNo;
    const isVinPresent = vin && vin.length > 0;
    const isDealerPresent = dealerNumber && dealerNumber.length > 0;

    if (isVinPresent && isDealerPresent) {
      client
        .query({
          query: dupeExternalVehicleQuery,
          variables: { vin, dealerNumber },
        })
        .then(({ data }) => {
          const {
            errors,
            dupeExternalVehicle: { dealerName, status, locationInitials },
          } = data;

          const dupeVehicleObject = {
            vin,
            dealerName,
            status,
            dealerNumber: toString(dealerNumber),
            auctionName: locationInitials,
          };
          currentDispatch(addDupeExternalVehicle(dupeVehicleObject));
          if (errors) {
            setVinError(errors);
          }
        })
        .catch(error => {
          setVinError(`Error fetching info for ${vin} - ${error}`);
        });
    }
  };

  const findDupeEntryVehicle = (currentVehicles, index, currentDispatch) => {
    const recordvehicle = currentVehicles[index];
    const { vin } = recordvehicle;
    const dealerNumber = recordvehicle.dealerNo;
    const isVinPresent = vin && vin.length > 0;
    const isDealerPresent = dealerNumber && dealerNumber.length > 0;

    if (isVinPresent && isDealerPresent) {
      const duplicateEntryVehiclesToShow = [];
      currentDispatch(clearDupeEntryVehicle());
      map(currentVehicles, vehicle => {
        const existingVehicle = currentVehicles.filter(
          item => item.vin === vehicle.vin && item.dealerNo === vehicle.dealerNo
        );

        if (!isEmpty(existingVehicle) && existingVehicle.length > 1) {
          const existingDupeVehicle = duplicateEntryVehiclesToShow.filter(
            item => item.vin === vehicle.vin
              && item.dealerNumber === vehicle.dealerNo
          );
          if (isEmpty(existingDupeVehicle)) {
            const dupeVehicleObject = {
              vin: vehicle.vin,
              dealerName: vehicle.dealerName,
              dealerNumber: vehicle.dealerNo,
              auctionName: vehicle.locationInitials,
            };
            duplicateEntryVehiclesToShow.push(dupeVehicleObject);
          }
        }
      });
      if (!isEmpty(duplicateEntryVehiclesToShow)) {
        currentDispatch(addDupeEntryVehicle(duplicateEntryVehiclesToShow));
      }
    }
  };

  const dupeVehicleCheck = (currentVehicles, index, currentDispatch) => {
    findDupeVehicle(currentVehicles, index, currentDispatch);
    findDupeExternalVehicle(currentVehicles, index, currentDispatch);
    findDupeEntryVehicle(currentVehicles, index, currentDispatch);
  };

  const vinQuery = (
    currentVehicles,
    index,
    currentDispatch,
    checkMmr
  ) => () => {
    decodeVin(currentVehicles, index, currentDispatch);
    dupeVehicleCheck(currentVehicles, index, currentDispatch);
    checkMmr(currentVehicles, index, currentDispatch);
  };

  const purchasePriceQuery = (
    currentVehicles,
    index,
    currentDispatch,
    checkMmr
  ) => () => {
    calculateSum(currentVehicles, index, currentDispatch)();
    checkMmr(currentVehicles, index, currentDispatch);
  };

  const dealerQuery = (currentVehicles, index, currentDispatch) => () => {
    getDealerName(currentVehicles, index, currentDispatch);
    dupeVehicleCheck(currentVehicles, index, currentDispatch);
  };

  const removeDupeVin = (currentVehicles, index, currentFields, currentDispatch) => event => {
    event.preventDefault();
    const vehicle = currentVehicles[index];
    const isVinPresent = vehicle.vin && vehicle.vin.length > 0;
    const isDealerPresent = vehicle.dealerNo && vehicle.dealerNo.length > 0;

    if (isVinPresent && isDealerPresent) {
      currentDispatch(
        removeDupeVehicle({
          vin: vehicle.vin,
          dealerNumber: vehicle.dealerNo,
        })
      );
      currentDispatch(
        removeDupeExternalVehicle({
          vin: vehicle.vin,
          dealerNumber: vehicle.dealerNo,
        })
      );
      currentDispatch(
        removeDupeEntryVehicle({
          vin: vehicle.vin,
          dealerNumber: vehicle.dealerNo,
        })
      );
    }
    currentFields.remove(index);
  };

  return (
    <div
      style={{
        overflowX: 'scroll',
        overflowY: 'hidden',
        paddingBottom: '50px',
        whiteSpace: 'no-wrap',
      }}
    >
      {vinError && (
        <Message
          error
          content={vinError}
          onDismiss={handleDismiss}
        />
      )}
      {dealerErr && (
        <Message
          error
          visible
          content={dealerErr}
          onDismiss={handleDismiss}
        />
      )}
      {gettingInfo && (
        <Message
          info
          content={gettingInfo}
          onDismiss={handleDismiss}
        />
      )}
      <Table
        celled
        singleLine
        verticalAlign='top'
        style={{ width: '2200px' }}
      >
        {TableHeader}
        <Table.Body>
          {fields.map((vehicle, index) => (
            <Table.Row key={vehicle}>
              <Table.Cell className='table-field' style={{ width: '480px' }}>
                <Field
                  name={`${vehicle}.vin`}
                  placeholder='VIN'
                  type='text'
                  component={TextInput}
                  validate={[fieldRequired, alphaNumeric, hasLength17]}
                  onBlur={vinQuery(
                    vehicles,
                    index,
                    dispatch,
                    isVehiclePurchasePriceOverMmr
                  )}
                  parse={trimValue}
                />
              </Table.Cell>
              <Table.Cell
                className='year table-field'
                style={{ width: '10px' }}
              >
                <Field
                  name={`${vehicle}.year`}
                  placeholder='Year'
                  component={SelectDropdown(yearOptions())}
                  validate={[fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.make`}
                  placeholder='Make'
                  component={TextInput}
                  validate={[fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.model`}
                  placeholder='Model'
                  component={TextInput}
                  validate={[fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.odometerReading`}
                  placeholder='Odometer'
                  component={TextInput}
                  validate={[shouldBeNumber, fieldRequired, maxLength6]}
                  parse={parseValueToInt}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.dealerNo`}
                  placeholder='Dealer#'
                  component={TextInput}
                  onBlur={dealerQuery(vehicles, index, dispatch)}
                  validate={[shouldBeNumber, fieldRequired, shouldBe5mil]}
                />
              </Table.Cell>
              <Table.Cell className='table-field' style={{ width: '420px' }}>
                <Field
                  name={`${vehicle}.dealerName`}
                  placeholder='Dealer Name'
                  component={TextInput}
                  disabled
                  validate={[validDealer]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.sellerName`}
                  placeholder='Seller Name'
                  component={TextInput}
                  validate={[fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.vehiclePurchasePrice`}
                  placeholder='Purchase Price'
                  component={TextInput}
                  onBlur={purchasePriceQuery(
                    vehicles,
                    index,
                    dispatch,
                    isVehiclePurchasePriceOverMmr
                  )}
                  validate={[shouldBeNumber, fieldRequired, maxLength5]}
                  parse={parseValueToInt}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.buyFee`}
                  placeholder='Buy Fee'
                  component={TextInput}
                  onBlur={calculateSum(vehicles, index, dispatch)}
                  validate={[shouldBeNumber, fieldRequired, maxLength4]}
                  parse={parseValueToInt}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.vehicleTotal`}
                  placeholder='Vehicle Total'
                  component={TextInput}
                  validate={[shouldBeNumber, fieldRequired, maxLength5]}
                  parse={parseValueToInt}
                />
              </Table.Cell>
              <Table.Cell style={{ width: '300px' }}>
                <Field
                  name={`${vehicle}.locationInitials`}
                  placeholder='Location'
                  component={SelectDropdown(locationOptions)}
                  validate={[fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field' style={{ width: '320px' }}>
                <Field
                  name={`${vehicle}.automaticPurchaseEmail`}
                  placeholder='Email'
                  component={TextInput}
                  validate={[shouldBeEmail, fieldRequired]}
                />
              </Table.Cell>
              <Table.Cell className='table-field'>
                <Field
                  name={`${vehicle}.purchasedAt`}
                  placeholder='Guarantee Purchase Date'
                  component={DateInput}
                  validate={[fieldRequired]}
                  warn={[purchaseDateValidation(vehicles, index, dispatch)]}
                />
              </Table.Cell>
              <Table.Cell>
                <Button
                  icon
                  size='tiny'
                  color='red'
                  onClick={removeDupeVin(vehicles, index, fields, dispatch)}
                >
                  <Icon name='delete' />
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <br />
      <Button
        icon
        color='teal'
        labelPosition='left'
        onClick={() => fields.push({})}
      >
        <Icon name='plus' />
        Add Vehicle
      </Button>
      <Button type='submit' color='green'>
        Submit
      </Button>
    </div>
  );
};

NplusFieldRow.propTypes = {
  fields: PropTypes.object.isRequired,
  vehicles: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  duplicateVehicleIndex: PropTypes.array,
  duplicateExternalVehicleIndex: PropTypes.array,
  duplicateEntryVehicleIndex: PropTypes.array,
  data: PropTypes.shape({
    ftbLocations: PropTypes.array,
  }).isRequired,
  isVehiclePurchasePriceOverMmr: PropTypes.func.isRequired,
};

NplusFieldRow.defaultProps = {
  vehicles: [],
  duplicateVehicleIndex: [],
  duplicateExternalVehicleIndex: [],
  duplicateEntryVehicleIndex: [],
};

const mapStateToProps = state => {
  const formSelector = formValueSelector('npForm');
  const {
    data: {
      duplicateVehicleIndex,
      duplicateExternalVehicleIndex,
      duplicateEntryVehicleIndex,
    },
  } = state;
  return {
    vehicles: formSelector(state, 'vehicles'),
    duplicateVehicleIndex,
    duplicateExternalVehicleIndex,
    duplicateEntryVehicleIndex,
  };
};

export default compose(
  graphql(ftbLocationsQuery),
  connect(mapStateToProps),
  LoadingDataEnhancer
)(NplusFieldRow);
